/**
 * Pricing, in one place.
 *
 * The landing page and /pricing each hardcoded their own tier list, and drifted
 * to $6 and $5 respectively without anyone noticing. Both now render from here,
 * and a test asserts they agree — changing the number in one file cannot
 * silently contradict the other.
 */

export type Currency = 'CAD' | 'USD';

export interface Tier {
  name: string;
  /** Minor units, so no float arithmetic on money. */
  monthly: Record<Currency, number>;
  blurb: string;
  points: string[];
  cta: string;
  to: string;
  highlight?: boolean;
}

export const CURRENCY_SYMBOL: Record<Currency, string> = {
  CAD: 'CA$',
  USD: 'US$',
};

/** Global default. Canada is the exception, detected per request. */
export const DEFAULT_CURRENCY: Currency = 'USD';

export const TIERS: Tier[] = [
  {
    name: 'Free',
    monthly: { CAD: 0, USD: 0 },
    blurb: 'Track it by hand, properly.',
    points: [
      'Full double-entry ledger',
      'Unlimited accounts and manual entries',
      'Money-flow map and net-worth history',
      'Bills, payment grid, budgets and goals',
      'One household member',
    ],
    cta: 'Start free',
    to: '/register',
  },
  {
    name: 'Household',
    monthly: { CAD: 700, USD: 500 },
    blurb: 'Stop typing in transactions.',
    highlight: true,
    points: [
      'Everything in Free',
      // Plaid is deliberately NOT sold on any tier. The instance shares one
      // small, fixed pool of Plaid Items, so a self-serve bank link consumes a
      // paid slot nobody else can then use. Advertising it while the backend
      // restricts it to the operator (migration 058) would be selling
      // something we do not deliver.
      'Import from your bank via the MCP server or CSV',
      'Automatic bill reconciliation',
      'Up to five household members',
      'Per-lot cost basis tracking',
      'CSV and API export',
    ],
    cta: 'Start free trial',
    to: '/register',
  },
];

/**
 * Renders a tier price.
 *
 * A currency symbol on a zero price is noise, so Free is just "Free".
 */
export function formatPrice(tier: Tier, currency: Currency): string {
  const cents = tier.monthly[currency];
  if (cents === 0) return 'Free';
  const whole = cents / 100;
  return `${CURRENCY_SYMBOL[currency]}${Number.isInteger(whole) ? whole : whole.toFixed(2)}`;
}

/** Cadence label. Free has no billing period to describe. */
export function formatCadence(tier: Tier): string {
  return tier.monthly.USD === 0 ? 'forever' : 'per month';
}
